"use client";
/**
 * Chapter navigation — the fixed side-rail scroll-spy from v6 (chapter-navigation.tsx),
 * ported verbatim (dependency-free). Scans `main > section`, skips heroes/headings,
 * labels each chapter (from the map or its h2), and highlights the in-view one after
 * 300px of scroll. Styled by v6's .chapter-navigation CSS. Renders nothing with < 2
 * chapters, so it only appears on the long editorial pages.
 */
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

const labels: Record<string, string> = {
    advantage: "Your advantage",
    "story-section": "The people",
    "network-experience": "The connections",
    "home-carriers": "Carrier partners",
    "carrier-section": "Carrier partners",
    "home-media": "The podcast",
    "faq-section": "Your questions",
    "product-lines": "The portfolio",
    "toolkit-section": "Your toolkit",
    "readiness-section": "Ready to sell",
    "leads-intro": "Leads & marketing",
    "compliance-panel": "Confidence & clarity",
    "events-feature": "Get in the room",
    "event-recaps": "From the field",
    "ahip-band": "Get certified",
    "about-story": "Our commitment",
    "media-feature": "The conversation",
    "media-library": "The archive",
    "real-talk-strip": "Real Talk",
    "contact-grid": "Let's talk",
    "contract-section": "Choose your path",
    "career-section": "Join the team",
    "faq-wide": "Straight answers",
    "partner-content": "Your carrier partner",
    "article-layout": "The story",
    "legal-page": "The details",
    "cta-strip": "Your next move",
};

export default function ChapterNavigation() {
    const path = usePathname();
    const [chapters, setChapters] = useState<{ id: string; label: string }[]>([]);
    const [active, setActive] = useState("");
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const sections = Array.from(
            document.querySelectorAll<HTMLElement>("main > section"),
        ).filter(
            (s) =>
                !s.matches(
                    ".page-hero,.article-heading,.immersive-hero,.flight-hero",
                ),
        );
        const entries = sections.flatMap((section, i) => {
            const label = Object.keys(labels).find((key) =>
                section.classList.contains(key),
            );
            const title = label
                ? labels[label]
                : section
                      .querySelector("h2")
                      ?.textContent?.replace(/\s+/g, " ")
                      .trim();
            if (!title) return [];
            if (!section.id) section.id = `chapter-${i + 1}`;
            return [
                {
                    id: section.id,
                    label:
                        title.length > 37 ? title.slice(0, 34) + "…" : title,
                },
            ];
        });
        setChapters(entries);
        setActive(entries[0]?.id || "");
        const observer = new IntersectionObserver(
            (items) =>
                items.forEach((item) => {
                    if (item.isIntersecting) setActive(item.target.id);
                }),
            { rootMargin: "-22% 0px -55% 0px" },
        );
        entries.forEach(({ id }) => {
            const element = document.getElementById(id);
            if (element) observer.observe(element);
        });
        const show = () => setVisible(window.scrollY > 300);
        show();
        window.addEventListener("scroll", show, { passive: true });
        return () => {
            observer.disconnect();
            window.removeEventListener("scroll", show);
        };
    }, [path]);

    if (chapters.length < 2) return null;
    return (
        <nav
            className="chapter-navigation"
            data-visible={visible}
            aria-label="Page chapters"
        >
            <ol>
                {chapters.map((chapter, i) => (
                    <li key={chapter.id}>
                        <a
                            href={"#" + chapter.id}
                            aria-current={
                                active === chapter.id ? "location" : undefined
                            }
                            aria-label={`Chapter ${i + 1}: ${chapter.label}`}
                        >
                            <span className="chapter-dot" />
                            <span className="chapter-nav-label">
                                <b className="mono">
                                    {String(i + 1).padStart(2, "0")}
                                </b>
                                {chapter.label}
                            </span>
                        </a>
                    </li>
                ))}
            </ol>
        </nav>
    );
}
